import { QueryClient } from '@tanstack/react-query'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import {
  createSharedPluginFlush,
  flushPluginFunctions,
  type PluginFlushFunction,
} from '@/lib/plugins/plugin-flush'

export type { PluginFlushOptions } from '@/lib/plugins/plugin-flush'

type RegisteredFlusher = {
  scopeKey: string
  fn: PluginFlushFunction
  getFingerprint?: () => unknown
}

const flushers = new Map<string, RegisteredFlusher>()
const sharedFlush = createSharedPluginFlush()

// Incremented after every commit of a component that participates in flush
// observation (each usePluginMutation call site, plus the activation CTA).
// Lets the flush wait for React to commit refetched data into render refs.
let commitSeq = 0

export function bumpPluginCommitSeq() {
  commitSeq++
}

const nextMacrotask = () => new Promise<void>((resolve) => setTimeout(resolve, 0))

// Bounded wait for a commit bump. Proceeds on expiry so an invalidation that
// legitimately produces no commit (e.g. a disabled query with no observer)
// can never wedge the shared flush — the consecutive-clean-pass fingerprint
// gate in flushPluginFunctions carries correctness, not this wait.
const MAX_COMMIT_WAIT_YIELDS = 10

async function awaitCommits() {
  const seqBefore = commitSeq
  for (let i = 0; i < MAX_COMMIT_WAIT_YIELDS; i++) {
    await nextMacrotask()
    if (commitSeq > seqBefore) return
  }
}

// Matches the activation quiescence gate: a save's dependent refetches are the
// same network work that gate already waited on, so give them the same budget.
const READINESS_TIMEOUT_MS = 15_000

// Liveness fallback only. Readiness derives from query `dataUpdatedAt`, so a
// finishing refetch always emits a cache event; the tick just guarantees a
// re-check if some readiness input ever changes without one.
const READINESS_EVENT_MAX_WAIT_MS = 500

function awaitCacheEvent(queryClient: QueryClient) {
  return new Promise<void>((resolve) => {
    const unsubscribes: (() => void)[] = []
    const settle = () => {
      clearTimeout(timeout)
      while (unsubscribes.length) unsubscribes.pop()?.()
      resolve()
    }
    const timeout = setTimeout(settle, READINESS_EVENT_MAX_WAIT_MS)
    unsubscribes.push(queryClient.getQueryCache().subscribe(settle), queryClient.getMutationCache().subscribe(settle))
  })
}

// A plugin reporting "not ready" mid-flush is waiting on a refetch the blur
// save triggered — quiescence covers the mutation, not the queries it
// invalidates. Wait for the cache to move, then for the commit that pushes the
// new data into the plugins' render refs.
async function awaitReadinessChange(queryClient: QueryClient) {
  await awaitCacheEvent(queryClient)
  await awaitCommits()
}

function combinedFingerprint() {
  const parts: [string, unknown][] = []
  for (const [id, flusher] of flushers) {
    parts.push([id, flusher.getFingerprint?.()])
  }
  parts.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return JSON.stringify(parts)
}

export function registerPluginFlush(
  id: string,
  scopeKey: string,
  fn: PluginFlushFunction,
  getFingerprint?: () => unknown
) {
  flushers.set(id, { scopeKey, fn, getFingerprint })
  return () => {
    flushers.delete(id)
  }
}

async function drainInvalidations(queryClient: QueryClient) {
  const { queryKeys, removeQueryKeys } = usePluginQueryKeysToInvalidateStore.getState()
  if (!queryKeys.length) return false
  await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
  removeQueryKeys(queryKeys)
  return true
}

// Per-budget cap: mutation passes (details → tax → quote → milestone chain
// plus refetch-cycle buffer) and verification/not-ready passes are counted
// separately in flushPluginFunctions, so the two-pass fingerprint gate never
// starves the mutation chain.
const MAX_FLUSH_ITERATIONS = 8

// A cold flush can legitimately take a while: up to MAX_FLUSH_ITERATIONS of the
// plugin dependency chain, each PATCHing summary rows and refetching the full
// quotedetails payload. The flush keeps running past a timeout (the race does
// not cancel it), so a retry usually completes quickly.
export async function flushAllPlugins(queryClient: QueryClient, quoteId: string, timeoutMs = 30_000) {
  const work = sharedFlush(quoteId, () => {
    const registered = Array.from(flushers.values())
    if (!registered.length || registered.some((flusher) => flusher.scopeKey !== quoteId)) {
      throw new Error('Quote calculations changed scope. Refresh the quote and try again.')
    }
    return flushPluginFunctions(
      registered.map((flusher) => flusher.fn),
      () => drainInvalidations(queryClient),
      MAX_FLUSH_ITERATIONS,
      {
        fingerprint: combinedFingerprint,
        awaitCommits,
        awaitReadinessChange: () => awaitReadinessChange(queryClient),
        readinessTimeoutMs: READINESS_TIMEOUT_MS,
      }
    )
  })

  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Quote calculations are still syncing. Please try again in a moment.')),
      timeoutMs
    )
  })

  try {
    await Promise.race([work, timeout])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}
